import type { Pixel } from "@pdfking/math"
import type { TaskContext } from "@pdfking/taskmaker"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import type { SinglePageImage } from "../types/files/cached/SinglePageImage"
import type { SinglePagePdf } from "../types/files/cached/SinglePagePdf"
import type { EngineTasks } from "../types/tasks/EngineTasks"
import { PDFRasterize } from "@pdfking/renderer-default"
import { First, Range } from "@pdfking/tfunc"
import { GenerateFile } from "../files/GenerateFile"
import { RequestBlob } from "../files/RequestBlob"
import { ConvertPixels } from "../geometry/ConvertPixels"
import { ContentType } from "../types/files/ContentType"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { TrackedTaskName } from "../types/tasks/TrackedTaskName"

const singlePage = (page_count: number) => {
  if (page_count !== 1) {
    throw new Error(`Expected 1 page, got ${page_count}`)
  }
  return Range(1, 1)
}

export const TaskRasterize = async function* ({
  emit,
  engine,
  options,
  source,
}: TaskContext<
  EngineTasks,
  {
    readonly options: PdfEngineOptions
    readonly source: SinglePagePdf
  }
>): AsyncGenerator<SinglePageImage> {
  void emit({
    done: 0,
    name: TrackedTaskName.RASTERIZE_PDF,
    total: 1,
  })
  const { size, stream } = await RequestBlob(engine, options, source, emit)
  const input = { size, stream, type: ContentType.PDF }

  const result = await First(PDFRasterize(input, options.image_dpi, singlePage))
  const content = await GenerateFile(
    engine,
    options,
    [result.blob],
    ContentType.PNG,
    emit
  )

  yield {
    enum: VirtualFileType.SINGLE_PAGE_IMAGE,
    content,
    dpi: options.image_dpi,
    lpi: 0 as Pixel,
    size: ConvertPixels(options, {
      height: result.height,
      width: result.width,
    }),
    source,
    file_meta: {},
  } as SinglePageImage

  void emit({
    done: 1,
    name: TrackedTaskName.RASTERIZE_PDF,
    total: 1,
  })
}
